// src/dashboard/TherapistQuestions.js
import { useState, useEffect } from 'react';
import { MessageSquare, Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const TherapistQuestions = ({ user }) => {
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Fetch therapist's question responses
    useEffect(() => {
      const fetchResponses = async () => {
        if (!user) return;
        
        try {
          setLoading(true);
          
          const response = await fetch(`/api/therapist-responses/${user.id}`);
          
          if (!response.ok) {
            throw new Error('Failed to fetch responses');
          }
          
          const data = await response.json();
          setResponses(data.responses || []);
        } catch (error) {
          console.error('Error fetching therapist responses:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchResponses();
    }, [user]);
    
    // Filter responses by search term
    const filteredResponses = responses.filter(item => {
      const searchText = searchTerm.toLowerCase();
      return (
        item.question.toLowerCase().includes(searchText) ||
        item.answer.toLowerCase().includes(searchText)
      );
    });
    
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }
    
    if (responses.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <MessageSquare className="text-blue-600" size={32} />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Responses Yet</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            You haven't submitted any responses to the therapist questionnaire yet.
            Complete the questionnaire to build your profile.
          </p>
          <button
            onClick={() => window.location.href = '/therapist-questionnaire'}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Complete Questionnaire
          </button>
        </div>
      );
    }
    
    return (
      <div>
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="pl-10 w-full p-2 border border-gray-300 rounded-md"
              placeholder="Search your responses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="space-y-6">
          {filteredResponses.map((item) => (
            <div 
              key={item.id} 
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-800">{item.question}</h4>
                <div className="text-xs text-gray-500 mt-1">
                  Answered on {new Date(item.timestamp).toLocaleDateString()}
                </div>
              </div>
              
              <div className="p-4 bg-white">
                <div className="whitespace-pre-wrap">{item.answer}</div>
                
                {item.tags && item.tags.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-500 mb-2">Related Tags:</div>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 flex justify-end">
                  <button 
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    onClick={() => window.location.href = `/therapist-questionnaire?edit=${item.id}`}
                  >
                    Edit Response
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

export default TherapistQuestions;